import React, { useState, useRef, useEffect } from 'react'
import { AflAdrLosseKopersService } from 'services/AflAdrLosseKopersService'
import LinkDeliveryGroup from './LinkDeliveryGroup'
import LinkLocation from './LinkLocation'
import MessageModal from 'shared/components/MessageModal'
import { Answer, MessageType, VestigingEnum } from 'shared/models/Enum'
import { IAfleverafspraakGroepKoppeling } from 'shared/models/IAfleverafspraakGroepKoppeling'
import { IAfleverafspraakLocatieKoppeling } from 'shared/models/IAfleverafspraakLocatieKoppeling'
import { IKlantAfleverafspraak } from 'shared/models/IKlantAfleverafspraak'
import { IOrderType } from 'shared/models/IOrderType'
import {
  IModalObjectReference,
  IModalReference,
} from 'shared/models/References'
import {
  isAgreement,
  isGroupLink,
  isLocationLink,
  RowType,
} from './AgreementData'
import { IResponse } from 'shared/models/IResponse'
import { IKlant } from 'shared/models/IKlant'

interface IProps {
  customer: IKlant
  service: AflAdrLosseKopersService
  warehouse: VestigingEnum
  activeRow: RowType
  possibleOrderTypes: IOrderType[]
  onChangeCopy: (row: IAfleverafspraakLocatieKoppeling) => void
  onError: (errorMessage: string) => void
  onLoading: (isLoading: boolean) => void
  onReloadRequired: () => void
}

export default (props: IProps) => {
  const linkGroupRef =
    useRef<IModalObjectReference<IAfleverafspraakGroepKoppeling>>()
  const linkLocationRef =
    useRef<IModalObjectReference<IAfleverafspraakLocatieKoppeling>>()
  const deleteMessageRef = useRef<IModalReference>()

  const [copyRow, setCopyRow] = useState<IAfleverafspraakLocatieKoppeling>()

  const activeRowIsAgreement = (): boolean => {
    return isAgreement(props.activeRow)
  }

  const activeRowIsLocationLink = (): boolean => {
    return isLocationLink(props.activeRow)
  }

  const activeRowIsGroupLink = (): boolean => {
    return isGroupLink(props.activeRow)
  }

  const activeRowAsAgreement = (): IKlantAfleverafspraak => {
    if (activeRowIsAgreement()) {
      return props.activeRow as IKlantAfleverafspraak
    } else if (activeRowIsLocationLink()) {
      return activeRowAsLocationLink().klantAfleverafspraak
    } else if (activeRowIsGroupLink()) {
      return activeRowAsGroupLink().klantAfleverafspraak
    } else {
      return null
    }
  }

  const activeRowAsLocationLink = (): IAfleverafspraakLocatieKoppeling => {
    if (activeRowIsLocationLink) {
      return props.activeRow as IAfleverafspraakLocatieKoppeling
    } else {
      return null
    }
  }

  const activeRowAsGroupLink = (): IAfleverafspraakGroepKoppeling => {
    if (activeRowIsGroupLink) {
      return props.activeRow as IAfleverafspraakGroepKoppeling
    } else {
      return null
    }
  }

  const addGroupLinkClick = () => {
    linkGroupRef.current.show(null)
  }

  const addLocationLinkClick = () => {
    linkLocationRef.current.show(null)
  }

  const editClick = () => {
    if (activeRowIsLocationLink()) {
      linkLocationRef.current.show(activeRowAsLocationLink())
    } else if (activeRowIsGroupLink()) {
      linkGroupRef.current.show(activeRowAsGroupLink())
    }
  }

  const removeClick = () => {
    deleteMessageRef.current.show()
  }

  const getDeleteMessage = (): string => {
    let objectType = 'onbekend'
    if (activeRowIsLocationLink()) {
      objectType = 'locatiekoppeling'
    } else if (activeRowIsGroupLink()) {
      objectType = 'groepkoppeling'
    }

    return `Weet u zeker dat u deze ${objectType} wilt verwijderen?`
  }

  const deleteAnswer = (answer: Answer) => {
    if (answer === Answer.Yes) {
      if (activeRowIsLocationLink()) {
        const request = props.service.deleteLocationLink(
          props.warehouse,
          props.customer.klantNummer,
          activeRowAsLocationLink().afleverafspraakLocatieKoppelingId
        )
        handleLoading(request)
      } else if (activeRowIsGroupLink()) {
        const request = props.service.deleteGroupLink(
          props.warehouse,
          props.customer.klantNummer,
          activeRowAsGroupLink().afleverafspraakGroepKoppelingId
        )
        handleLoading(request)
      }
    }
  }

  const handleLoading = (request: Promise<IResponse>) => {
    props.onLoading(true)
    request
      .then(result => {
        if (!result.success) {
          props.onError(result.errorMessage)
        } else {
          props.onError(null)
          props.onReloadRequired()
        }
      })
      .finally(() => {
        props.onLoading(false)
      })
  }

  const pasteLocationLinkClick = () => {
    const request = props.service.saveLocationLink(
      {
          afleverafspraakLocatieKoppelingId: 0,
          eindDatumtijd: copyRow.eindDatumtijd,
          ingangsDatumtijd: copyRow.ingangsDatumtijd,
          klantAfleverAfspraakId: activeRowAsAgreement().klantAfleverafspraakId,
          klantAfleverafspraak: activeRowAsAgreement(),
          shipToLocationGlnId: copyRow.shipToLocationGlnId,
          shipToLocationGln: copyRow.shipToLocationGln,
          magCombineren: false,
          routeToKlantId: copyRow.routeToKlantId,
          routeToLocationGlnId: copyRow.routeToLocationGlnId,
          koppelingType: copyRow.koppelingType,
          orderTypes: copyRow.orderTypes,
          dagen: copyRow.dagen
      },
      props.customer
    )
    handleLoading(request)
  }

  return (
    <React.Fragment>
      {!!props.customer && (
        <table>
          <tbody>
            <tr>
              <td>
                <div className='row no-gutters'>
                  <div className='col'></div>
                  {!copyRow && (
                    <div className='col-auto mr-2'>
                      <button
                        className='btn btn-primary'
                        disabled={!props.activeRow}
                        onClick={addGroupLinkClick}
                      >
                        Voeg groep toe
                      </button>
                    </div>
                  )}
                  {!copyRow && (
                    <div className='col-auto mr-2'>
                      <button
                        className='btn btn-primary'
                        disabled={!props.activeRow}
                        onClick={addLocationLinkClick}
                      >
                        Voeg koppeling toe
                      </button>
                    </div>
                  )}
                  {!!copyRow && (
                    <div className='col-auto mr-2'>
                      <button
                        className='btn btn-secondary'
                        onClick={() => setCopyRow(null)}
                      >
                        Stop kopieren
                      </button>
                    </div>
                  )}
                  {!!copyRow && (
                    <div className='col-auto mr-2'>
                      <button
                        className='btn btn-primary'
                        disabled={!activeRowIsAgreement()}
                        onClick={pasteLocationLinkClick}
                      >
                        Plakken koppeling
                      </button>
                    </div>
                  )}
                  {!copyRow && (
                    <div className='col-auto mr-2'>
                      <button
                        className='btn btn-primary'
                        disabled={!activeRowIsLocationLink()}
                        onClick={() => setCopyRow(activeRowAsLocationLink())}
                      >
                        Kopieren koppeling
                      </button>
                    </div>
                  )}
                  {!copyRow && (
                    <div className='col-auto mr-2'>
                      <button
                        className='btn btn-primary'
                        disabled={
                          !activeRowIsLocationLink() && !activeRowIsGroupLink()
                        }
                        onClick={() => editClick()}
                      >
                        Wijzigen
                      </button>
                    </div>
                  )}
                  {!copyRow && (
                    <div className='col-auto'>
                      <button
                        className='btn btn-primary'
                        disabled={
                          !activeRowIsLocationLink() && !activeRowIsGroupLink()
                        }
                        onClick={() => removeClick()}
                      >
                        Verwijder koppeling
                      </button>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <LinkDeliveryGroup
        customer={props.customer}
        service={props.service}
        ref={linkGroupRef}
        onSave={props.onReloadRequired}
        deliveryAgreement={activeRowAsAgreement()}
        warehouse={props.warehouse}
        possibleOrderTypes={props.possibleOrderTypes}
      />

      <LinkLocation
        customer={props.customer}
        service={props.service}
        ref={linkLocationRef}
        onSave={props.onReloadRequired}
        deliveryAgreement={activeRowAsAgreement()}
        warehouse={props.warehouse}
      />

      <MessageModal
        ref={deleteMessageRef}
        title='Verwijderen'
        message={getDeleteMessage()}
        messageType={MessageType.YesNo}
        onAnswer={deleteAnswer}
      />
    </React.Fragment>
  )
}
